import React, { useMemo, useState, useCallback, useEffect, useReducer, useRef } from 'react';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import useMeQuery from '@hooks/useMeQuery';
import { useNotificationsPageActionsApi } from '@metrics/client/react/useNotificationsPageActionsApi';
import { usePageActions } from '@cvent/sli-nextjs-metrics';
import useAppFeatures from '@hooks/useAppFeatures';
import getConfig from 'next/config';
import usePreferredTimezone from '@hooks/usePreferredTimezone';
import { useAuthExpired } from '@/apollo/authExpired';
import { usePubNub } from '../hooks/usePubNub';
import { messageEventHandler } from '../components/notifications/messageEventHandler';
import { messageActionHandler } from '../components/notifications/messageActionHandler';
import { getMessagesForChannels } from '../components/notifications/pubNubHistory';
import { getDeletedAnnouncementIdsFromMessageAction } from '../components/notifications/pubNubMessageAction';
import {
  isAttendeeReadChannel,
  formatNotifications,
  fetchNotificationIdsFromMessages,
  isPersistentNotificationChannel,
  isMessageActionsNeededForChannel,
  resolvePubnubError
} from '../components/notifications/utils';
import revokePubNubAuthKey from '../components/notifications/revokePubNubAuthKey';
import { updateAgendaDatesFromNotification } from '../components/mySchedule/updateAgendaDates';
import { SliEvents } from '../metrics/sliConstants';

const { publicRuntimeConfig } = getConfig();

const LOG = LoggerFactory.create('NotificationsContext');
const noOp = () => null;
const MAX_RETRIES = 3;

const resolveErrorFieldsForListener = event => {
  const { category, statusCode, operation, errorData } = event;
  const { response: { error: { url: pubNubUrl = '', message = '' } = {} } = {} } = errorData || {};

  if (pubNubUrl) {
    return {
      error: message,
      category,
      statusCode,
      operation,
      pubNubUrl
    };
  }
  return {
    category,
    statusCode,
    operation,
    errorData
  };
};

const NotificationsContext = React.createContext({
  state: {
    notifications: [],
    reminders: []
  },
  addNotifications: noOp,
  markAllNotificationsAsRead: noOp,
  markNotificationsAsRead: noOp,
  deleteNotifications: noOp,
  dismissReminders: _reminderIds => null,
  displayReminders: _reminderIds => null,
  // TODO remove this in favour of calling useApolloClient directly
  setApolloClient: _client => null,
  revokePubNubAuthKey: noOp
});

const NOTIFICATION_REDUCER_ACTIONS = Object.freeze({
  ADD: 'ADD',
  ADD_REMINDER: 'ADD_REMINDER',
  DISMISS_REMINDER: 'DISMISS_REMINDER',
  DISPLAY_REMINDER: 'DISPLAY_REMINDER',
  MARK_ALL_READ: 'MARK_ALL_READ',
  MARK_READ: 'MARK_READ',
  RESET: 'RESET',
  DELETE: 'DELETE'
});

const HTTP_ERROR_CODES = {
  FORBIDDEN: 403
};

const PUB_NUB_STATUS_CODES = {
  PN_NETWORK_UP_CATEGORY: 'PNNetworkUpCategory',
  PN_SUBSCRIPTION_OPERATION: 'PNSubscribeOperation'
};

const RETRY_PUBNUB_CODE = ['PNNetworkIssuesCategory', 'PNAccessDeniedCategory'];

export const reducer = (state, action) => {
  const { notifications, reminders } = state;
  switch (action.type) {
    case NOTIFICATION_REDUCER_ACTIONS.ADD: {
      const { notifications: notificationsToAppend } = action.payload;
      const sortedNotifications = [...notifications, ...notificationsToAppend].sort(
        ({ publishedAt: date1 }, { publishedAt: date2 }) => new Date(date2).getTime() - new Date(date1).getTime()
      );
      return {
        ...state,
        notifications: sortedNotifications
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.MARK_ALL_READ: {
      const allReadNotifications = notifications.map(notification => ({
        ...notification,
        isRead: true
      }));
      return {
        ...state,
        notifications: allReadNotifications
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.MARK_READ: {
      const { notificationIds = [] } = action.payload;
      if (!notificationIds.length) {
        return state;
      }
      const updatedNotifications = notifications.map(notification =>
        notificationIds.includes(notification.id) ? { ...notification, isRead: true } : { ...notification }
      );
      // would want to return the updated announcements here so that repeated announcements
      // are not published as a message to pubNub.
      return {
        ...state,
        notifications: updatedNotifications
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.RESET: {
      return {
        ...state,
        notifications: []
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.DELETE: {
      const { notificationIds = [] } = action.payload;
      const filteredNotifications = notifications.filter(notification => !notificationIds.includes(notification.id));
      return {
        ...state,
        notifications: filteredNotifications
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.ADD_REMINDER: {
      const { reminders: remindersToAppend } = action.payload;
      return {
        ...state,
        reminders: [...reminders, ...remindersToAppend]
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.DISMISS_REMINDER: {
      const { reminderIds = [] } = action.payload;
      if (!reminderIds.length) {
        return state;
      }
      const updatedReminders = reminders.map(reminder =>
        reminderIds.includes(reminder.messageId) ? { ...reminder, dismiss: true } : reminder
      );
      return {
        ...state,
        reminders: updatedReminders
      };
    }
    case NOTIFICATION_REDUCER_ACTIONS.DISPLAY_REMINDER: {
      const { reminderIds = [] } = action.payload;
      if (!reminderIds.length) {
        return state;
      }
      const updatedReminders = reminders.map(reminder =>
        reminderIds.includes(reminder.messageId) ? { ...reminder, display: true } : reminder
      );
      return {
        ...state,
        reminders: updatedReminders
      };
    }
    default: {
      return state;
    }
  }
};

/**
 * This method is used to fetch messages from history and add or mark the notifications as read.
 * @param {Object} | object containing channels, pubNub client, eventId, attendeeId and
 * methods for updating state when the notification is added or marked read.
 */
const fetchNotificationsFromHistory = async ({
  channels,
  pubNubClient,
  attendeeId,
  eventId,
  addNotifications,
  markNotificationsAsRead,
  trackAction
}) => {
  const notificationChannels = channels.filter(channel => isPersistentNotificationChannel(channel));
  const notificationReadStatusChannels = channels.filter(channel => isAttendeeReadChannel(channel));
  const formatAndCreateNotifications = ({ messages }) => {
    const formattedNotifications = formatNotifications(messages);
    addNotifications(formattedNotifications);
  };
  const getNotificationIdsAndMarkRead = ({ messages }) => {
    const notificationIds = fetchNotificationIdsFromMessages(messages);
    markNotificationsAsRead(notificationIds);
  };
  await getMessagesForChannels({
    attendeeId,
    eventId,
    pubNubClient,
    channels: notificationChannels,
    successCallback: formatAndCreateNotifications,
    trackAction
  });
  // finish loading
  await getMessagesForChannels({
    attendeeId,
    eventId,
    pubNubClient,
    channels: notificationReadStatusChannels,
    successCallback: getNotificationIdsAndMarkRead,
    trackAction
  });
};

/**
 * This method is used to fetch messages actions.
 * @param {Object} | object containing channels, pubNub client, eventId, attendeeId and
 * methods for updating state when the notification is deleted.
 */
const fetchMessageActions = async ({
  channels,
  pubNubClient,
  attendeeId,
  eventId,
  deleteNotifications,
  trackAction
}) => {
  const channelsThatNeedMessageActions = channels.filter(channel => isMessageActionsNeededForChannel(channel));

  await getDeletedAnnouncementIdsFromMessageAction({
    channels: channelsThatNeedMessageActions,
    pubNubClient,
    attendeeId,
    eventId,
    successCallback: deleteNotifications,
    trackAction
  });
};

/**
 * publishes message to pubNub and retries for 403 errors
 * @param {Object} pubNubClient pubNub client
 * @param {Function} setPubNubConfig  setter method
 * @param {Function} fetchPubNubConfig get PubNub config
 * @param {Object} message  message to be publshed
 * @param {String} channel name of channel
 * @param {Number} publishRetryCount retry count for publish
 * @returns
 */
export const publishMessageToPubNub = async (
  pubNubClient,
  setPubNubConfig,
  fetchPubNubConfig,
  message,
  channel,
  publishRetryCount = 0
) => {
  try {
    LOG.debug('publishing message to pubNub', {
      message,
      channel,
      publishRetryCount
    });
    if (publishRetryCount < MAX_RETRIES) {
      await pubNubClient.publish({
        message,
        channel
      });
      LOG.debug('published message to PubNub');
    }
  } catch (err) {
    if (publishRetryCount === MAX_RETRIES) {
      const errorData = resolvePubnubError(err);
      LOG.error(`failed to publish message to channel: ${message.channel} eventId: ${message.eventId}`, errorData);
      throw err;
    }

    if (err.status?.statusCode === HTTP_ERROR_CODES.FORBIDDEN) {
      const pubNubConfig = await fetchPubNubConfig();
      pubNubClient.setAuthKey(pubNubConfig?.authKey);
      pubNubClient.subscribe({
        channels: pubNubConfig?.channels
      });
      setPubNubConfig(pubNubConfig);
    }
    return publishMessageToPubNub(
      pubNubClient,
      setPubNubConfig,
      fetchPubNubConfig,
      message,
      channel,
      publishRetryCount + 1
    );
  }
  return null;
};

function NotificationsProvider({ children }: { children?: React.ReactNode }): JSX.Element {
  const { seamlessTransitionToastFake } = useAppFeatures();
  const { trackAction } = usePageActions();
  const { data: { me: { id: attendeeId, event: { id: eventId } = {} } = {} } = {} } = useMeQuery();
  const [state, dispatch] = useReducer(reducer, {
    notifications: [],
    reminders: []
  });
  const environment = publicRuntimeConfig.ENVIRONMENT_NAME;
  const { getPubNubClient, getCachedPubNubClient, fetchPubNubConfig } = usePubNub();
  const { notificationDelivered, notificationRead } = useNotificationsPageActionsApi();
  const eventListeners = useRef();
  const { preferredTimezone } = usePreferredTimezone();
  const [apolloClient, setApolloClient] = useState(null);
  const [pubNubConfig, setPubNubConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const retries = useRef(0);
  const { authExpired } = useAuthExpired();
  const [notificationsDeliveredCount, setNotificationsDeliveredCount] = useState(0);
  const addNotifications = useCallback(
    notifications => {
      dispatch({
        type: NOTIFICATION_REDUCER_ACTIONS.ADD,
        payload: {
          notifications
        }
      });
      updateAgendaDatesFromNotification(apolloClient?.cache, notifications, preferredTimezone);
    },
    [dispatch, apolloClient, preferredTimezone]
  );

  const markAllNotificationsAsRead = useCallback(() => {
    dispatch({
      type: NOTIFICATION_REDUCER_ACTIONS.MARK_ALL_READ
    });
  }, [dispatch]);

  const resetNotifications = useCallback(() => {
    dispatch({
      type: NOTIFICATION_REDUCER_ACTIONS.RESET
    });
  }, [dispatch]);

  const publishToAttendeeReadStatusChannel = useCallback(
    message =>
      pubNubConfig.channels &&
      (!authExpired || (retries.current < MAX_RETRIES && getCachedPubNubClient())) &&
      publishMessageToPubNub(
        getCachedPubNubClient(),
        setPubNubConfig,
        fetchPubNubConfig,
        { ...message, eventId, environment },
        pubNubConfig.channels.filter(isAttendeeReadChannel)[0]
      ),
    [pubNubConfig, authExpired, fetchPubNubConfig, eventId, environment, setPubNubConfig, getCachedPubNubClient]
  );
  const publishReadMessage = useCallback(
    notificationIds => {
      let i = 0;
      while (i < notificationIds.length) {
        const notificationIdsToPublish = notificationIds.slice(i, i + 50);
        const message = {
          announcementIds: notificationIdsToPublish
        };
        publishToAttendeeReadStatusChannel(message);
        i += 50;
      }
    },
    [publishToAttendeeReadStatusChannel]
  );
  const markNotificationsAsRead = useCallback(
    notificationIds => {
      dispatch({
        type: NOTIFICATION_REDUCER_ACTIONS.MARK_READ,
        payload: {
          notificationIds
        }
      });
    },
    [dispatch]
  );
  const markAndPublishNotificationsAsRead = useCallback(
    notificationIds => {
      markNotificationsAsRead(notificationIds);
      notificationRead({
        notificationIds,
        // @ts-expect-error TS(2322) FIXME: Type 'boolean' is not assignable to type 'string'.
        isRead: true
      });
      publishReadMessage(notificationIds);
    },
    [notificationRead, markNotificationsAsRead, publishReadMessage]
  );
  const deleteNotifications = useCallback(
    notificationIds => {
      dispatch({
        type: NOTIFICATION_REDUCER_ACTIONS.DELETE,
        payload: {
          notificationIds
        }
      });
    },
    [dispatch]
  );

  // Seamless transition toast functions
  const addReminders = useCallback(
    reminders => {
      dispatch({
        type: NOTIFICATION_REDUCER_ACTIONS.ADD_REMINDER,
        payload: {
          reminders
        }
      });
    },
    [dispatch]
  );
  const dismissReminders = useCallback(
    reminderIds => {
      dispatch({
        type: NOTIFICATION_REDUCER_ACTIONS.DISMISS_REMINDER,
        payload: {
          reminderIds
        }
      });
    },
    [dispatch]
  );
  const displayReminders = useCallback(
    reminderIds => {
      dispatch({
        type: NOTIFICATION_REDUCER_ACTIONS.DISPLAY_REMINDER,
        payload: {
          reminderIds
        }
      });
    },
    [dispatch]
  );

  // fetch pubNub config
  useEffect(() => {
    (async () => {
      if (apolloClient && !pubNubConfig && !authExpired) {
        try {
          const configs = await fetchPubNubConfig();
          setPubNubConfig(configs);
        } catch (err) {
          LOG.error(`Error fetching pubNub config for eventId: ${eventId}`, err);
        }
      }
    })();
  }, [apolloClient, pubNubConfig, eventId, authExpired, fetchPubNubConfig]);

  // initialises pubNubClient, attach event listeners and subscribe channels
  useEffect(() => {
    const effect = async () => {
      try {
        if (!pubNubConfig || authExpired || retries.current === MAX_RETRIES) {
          return () => null;
        }
        let pubNubClient = getCachedPubNubClient();
        const { publishKey, subscribeKey, authKey, channels } = pubNubConfig;

        if (!pubNubClient) {
          // @ts-expect-error TS(2322) FIXME: Type '{ status: (event: any) => void; message: (su... Remove this comment to see the full error message
          eventListeners.current = {
            status: event => {
              const { error, category, statusCode, operation } = event;
              if (!error) {
                LOG.debug('PubNub status event listener:', event);
                if (retries.current) {
                  // reset the retry count to 0 if it is greater than 0 and we get a success in between
                  retries.current = 0;
                }
                if (category === PUB_NUB_STATUS_CODES.PN_NETWORK_UP_CATEGORY) {
                  // eslint-disable-next-line no-use-before-define
                  resetAndFetchNotifications();
                }
                // when subscription to pubnub event listener is successful
                trackAction(SliEvents.NOTIFICATION_PUBNUB_EVENT_LISTENER, {
                  result: 'success',
                  channels
                });
                return;
              }
              /**
               * This log would log every error that the client is seeing
               * would help us debug the increase in unauthorised subscribe calls
               * and catch other possible errors.
               */
              LOG.warn('pubNub status event listener error', {
                event,
                attendeeId,
                eventId,
                environment,
                authKey,
                channels
              });
              if (
                RETRY_PUBNUB_CODE.includes(category) &&
                operation === PUB_NUB_STATUS_CODES.PN_SUBSCRIPTION_OPERATION &&
                retries.current < MAX_RETRIES
              ) {
                retries.current += 1;
                if (statusCode === HTTP_ERROR_CODES.FORBIDDEN) {
                  // setting config to null and re-fetching
                  setPubNubConfig(null);
                }
              } else {
                const errorDetails = resolveErrorFieldsForListener(event);
                const errorMessage = 'Error in pubNub status listener event';
                trackAction(SliEvents.NOTIFICATION_PUBNUB_EVENT_LISTENER, {
                  result: 'error',
                  retryCount: retries.current,
                  error: errorMessage,
                  ...errorDetails
                });
                LOG.error(errorMessage, {
                  ...errorDetails,
                  retryCount: retries.current
                });
              }
            },
            message: subscriptionMessage =>
              messageEventHandler({
                subscriptionMessage,
                addNotifications,
                markNotificationsAsRead,
                addReminders,
                seamlessTransitionToastFake
              }),
            messageAction: message => {
              messageActionHandler({
                message,
                deleteNotifications
              });
            }
          };
          pubNubClient = await getPubNubClient({
            eventListeners: eventListeners.current,
            config: {
              publishKey,
              subscribeKey,
              authKey,
              uuid: attendeeId
            }
          });

          const resetAndFetchNotifications = async () => {
            setLoading(true);
            // adding this here because during development, this useEffect is called everytime the page refreshes
            // and we keep on adding duplicate notifications. Hence, setting resetting it.
            resetNotifications();
            await fetchNotificationsFromHistory({
              channels,
              pubNubClient,
              attendeeId,
              eventId,
              addNotifications,
              markNotificationsAsRead,
              trackAction
            });
            // to get deleted notifications Ids using getMessageActions PubNub API
            await fetchMessageActions({
              channels,
              pubNubClient,
              attendeeId,
              eventId,
              deleteNotifications,
              trackAction
            });
            setLoading(false);
          };
          resetAndFetchNotifications();
        } else {
          pubNubClient.setAuthKey(authKey);
        }
        pubNubClient.subscribe({ channels });
      } catch (err) {
        const errorData = resolvePubnubError(err);
        const errorMessage = 'Error while trying to initialize pubNub client, attach eventListener';
        trackAction(SliEvents.NOTIFICATION_PUBNUB_EVENT_LISTENER, {
          result: 'error',
          error: errorMessage,
          ...errorData
        });
        LOG.error(`${errorMessage},  for eventId: ${eventId}, attendeeId: ${attendeeId}`, err);
        setLoading(false);
      }
    };
    effect();
    return () => {
      const cachedPubNubClient = getCachedPubNubClient();
      if (cachedPubNubClient) {
        cachedPubNubClient.unsubscribeAll();
      }
    };
  }, [
    addNotifications,
    markNotificationsAsRead,
    pubNubConfig,
    attendeeId,
    eventId,
    environment,
    getPubNubClient,
    getCachedPubNubClient,
    resetNotifications,
    authExpired,
    deleteNotifications,
    addReminders,
    seamlessTransitionToastFake,
    trackAction
  ]);

  // Tracks Delivery fact for notifications
  useEffect(() => {
    const attendeeAnnouncementIds: string[] = Array.from(
      new Set(
        state.notifications
          .slice(0, state.notifications.length - notificationsDeliveredCount)
          .map(notification => notification.id)
      )
    );
    if (attendeeAnnouncementIds.length) {
      notificationDelivered({
        notificationDelivered: attendeeAnnouncementIds
      });
    }
    setNotificationsDeliveredCount(state.notifications.length);
  }, [state.notifications, notificationsDeliveredCount, notificationDelivered]);

  const contextValue = useMemo(
    () => ({
      state,
      addNotifications,
      markAllNotificationsAsRead,
      markAndPublishNotificationsAsRead,
      dismissReminders,
      displayReminders,
      loading,
      setApolloClient,
      revokePubNubAuthKey: () => revokePubNubAuthKey(apolloClient, pubNubConfig.authKey)
    }),
    [
      state,
      markAndPublishNotificationsAsRead,
      addNotifications,
      markAllNotificationsAsRead,
      dismissReminders,
      displayReminders,
      loading,
      setApolloClient,
      apolloClient,
      pubNubConfig
    ]
  );
  // called when the component un-mounts
  useEffect(
    () => () => {
      const pubNubClient = getCachedPubNubClient();
      if (pubNubClient && pubNubConfig) {
        pubNubClient.unsubscribe({ channels: pubNubConfig.channels });
        if (eventListeners.current) {
          pubNubClient.removeListener(eventListeners.current);
        }
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const cachedClient = getCachedPubNubClient();
    if (cachedClient && (authExpired || retries.current === MAX_RETRIES)) {
      if (eventListeners.current) {
        cachedClient.removeListener(eventListeners.current);
      }
      cachedClient.stop();
    }
  }, [authExpired, retries, eventListeners, getCachedPubNubClient]);

  // @ts-expect-error TS(2739) FIXME: Type '{ state: any; addNotifications: (notificatio... Remove this comment to see the full error message
  return <NotificationsContext.Provider value={contextValue}>{children}</NotificationsContext.Provider>;
}

export { NotificationsProvider, NotificationsContext, NOTIFICATION_REDUCER_ACTIONS };
